import { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { subscriptionVendorAPI } from '../lib/api';
import { getErrorMessage } from '../lib/errors';

/**
 * Maintainable mapping of recognized recurring tool vendors (services/
 * subscription_vendors.py) -- a supplier invoice whose vendor normalizes
 * to one of these rows auto-creates/updates ONE Subscription record for
 * that (entity, vendor). Matching is STRICT normalized-name equality, not
 * a substring test; this panel only edits the list, never the matching
 * logic itself.
 */
export function RecognizedSubscriptionVendorsPanel() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newVendorName, setNewVendorName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await subscriptionVendorAPI.list();
      setVendors(data?.vendors ?? []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVendors(); }, [loadVendors]);

  const handleAdd = async () => {
    const name = newVendorName.trim();
    if (!name) return;
    setSubmitting(true);
    setError(null);
    try {
      await subscriptionVendorAPI.create(name);
      setNewVendorName('');
      await loadVendors();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (vendorId) => {
    setRemovingId(vendorId);
    setError(null);
    try {
      await subscriptionVendorAPI.remove(vendorId);
      await loadVendors();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card data-testid="recognized-subscription-vendors-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Recognized subscription vendors</CardTitle>
        <p className="text-xs text-muted-foreground">
          A supplier invoice from one of these vendors auto-creates or updates one
          Subscription record per entity. Vendor matching is strict: exact normalized name,
          or the invoice's full legal name reducing to the same brand once generic
          qualifiers ("Technologies", "Labs", "Inc", ...) are stripped -- never a plain
          substring search.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            value={newVendorName}
            onChange={(e) => setNewVendorName(e.target.value)}
            placeholder="Vendor name, e.g. Anthropic"
            data-testid="recognized-vendor-input"
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          />
          <Button
            size="sm" onClick={handleAdd} disabled={submitting || !newVendorName.trim()}
            data-testid="recognized-vendor-add"
          >
            {submitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Add
          </Button>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : vendors.length === 0 ? (
          <p className="text-xs text-muted-foreground">No recognized vendors yet.</p>
        ) : (
          <ul className="space-y-1" data-testid="recognized-vendor-list">
            {vendors.map((v) => (
              <li key={v.id} className="flex items-center justify-between text-sm border-b py-1">
                <span>
                  <span data-testid={`recognized-vendor-name-${v.id}`}>{v.vendor_name}</span>
                  {v.aliases?.length > 0 && (
                    <span
                      className="ml-2 text-[10px] text-muted-foreground"
                      data-testid={`recognized-vendor-aliases-${v.id}`}
                    >
                      aka {v.aliases.join(', ')}
                    </span>
                  )}
                </span>
                <Button
                  variant="ghost" size="sm" onClick={() => handleRemove(v.id)}
                  disabled={removingId === v.id}
                  data-testid={`recognized-vendor-remove-${v.id}`}
                >
                  {removingId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
